"use client";

import { useSyncExternalStore, useCallback } from "react";

const STORAGE_KEY = "recently-viewed-games";
const MAX_ITEMS = 6;

export type RecentlyViewedItem = {
  appid: number;
  name: string;
  capsule_image: string | null;
};

type RecentlyViewedStore = RecentlyViewedItem[];

const listeners = new Set<() => void>();

function getSnapshot(): string {
  if (typeof window === "undefined") return "[]";
  return localStorage.getItem(STORAGE_KEY) ?? "[]";
}

function getServerSnapshot(): string {
  return "[]";
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notify() {
  for (const listener of listeners) listener();
}

function readStore(): RecentlyViewedStore {
  try {
    return JSON.parse(getSnapshot()) as RecentlyViewedStore;
  } catch {
    return [];
  }
}

function writeStore(items: RecentlyViewedStore) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  notify();
}

export function useRecentlyViewedStore() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const items: RecentlyViewedStore = (() => {
    try {
      return JSON.parse(raw) as RecentlyViewedStore;
    } catch {
      return [];
    }
  })();

  const add = useCallback((item: RecentlyViewedItem) => {
    const current = readStore().filter((i) => i.appid !== item.appid);
    writeStore([item, ...current].slice(0, MAX_ITEMS));
  }, []);

  return { items, add };
}
